import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  BottomNavigation,
  BottomNavigationAction,
  Paper,
  Badge,
  Fab,
  Box,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Home as HomeIcon,
  HomeOutlined as HomeOutlinedIcon,
  AutoAwesome as PrayIcon,
  AutoAwesomeOutlined as PrayOutlinedIcon,
  Add as AddIcon,
  Group as GroupIcon,
  GroupOutlined as GroupOutlinedIcon,
  Person as PersonIcon,
  PersonOutlined as PersonOutlinedIcon,
} from '@mui/icons-material';

const navItems = [
  {
    label: 'Home',
    path: '/dashboard',
    icon: HomeOutlinedIcon,
    activeIcon: HomeIcon,
  },
  {
    label: 'Prayers',
    path: '/prayers',
    icon: PrayOutlinedIcon,
    activeIcon: PrayIcon,
  },
  {
    label: 'Create',
    path: '/create',
    icon: AddIcon,
    activeIcon: AddIcon,
    isFab: true,
  },
  {
    label: 'Circles',
    path: '/circles',
    icon: GroupOutlinedIcon,
    activeIcon: GroupIcon,
  },
  {
    label: 'Profile',
    path: '/profile',
    icon: PersonOutlinedIcon,
    activeIcon: PersonIcon,
  },
];

function BottomNav({ unreadCount = 0 }) {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Don't render on desktop
  if (!isMobile) return null;

  // Hide on certain pages
  const hiddenPaths = ['/login', '/', '/confirm', '/forgot-password', '/reset-password'];
  const shouldHide = hiddenPaths.some(path =>
    location.pathname === path || location.pathname.startsWith('/reset-password/')
  );
  if (shouldHide) return null;

  const getCurrentValue = () => {
    const currentPath = location.pathname;
    const index = navItems.findIndex(item =>
      item.path === currentPath ||
      (item.path === '/circles' && currentPath.startsWith('/circles/'))
    );
    return index >= 0 ? index : 0;
  };

  const handleChange = (event, newValue) => {
    const item = navItems[newValue];
    if (item && !item.isFab) {
      navigate(item.path);
    }
  };

  const handleCreateClick = () => {
    navigate('/create');
  };

  return (
    <Paper
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1100,
        pb: 'env(safe-area-inset-bottom)', // iOS safe area
      }}
      elevation={8}
    >
      <Box sx={{ position: 'relative' }}>
        {/* FAB Button in center */}
        <Box
          sx={{
            position: 'absolute',
            top: -28,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1,
          }}
        >
          <Fab
            color="primary"
            aria-label="Create prayer"
            onClick={handleCreateClick}
            sx={{
              width: 56,
              height: 56,
              boxShadow: '0 4px 20px rgba(44, 82, 130, 0.3)',
            }}
          >
            <AddIcon sx={{ fontSize: 28 }} />
          </Fab>
        </Box>

        <BottomNavigation
          value={getCurrentValue()}
          onChange={handleChange}
          showLabels
          sx={{
            height: 64,
            '& .MuiBottomNavigationAction-root': {
              minWidth: 'auto',
              padding: '8px 0 12px',
              '&.Mui-selected': {
                padding: '6px 0 10px',
              },
            },
            '& .MuiBottomNavigationAction-label': {
              fontSize: '0.7rem',
              marginTop: '2px',
              '&.Mui-selected': {
                fontSize: '0.7rem',
              },
            },
          }}
        >
          {navItems.map((item, index) => {
            const isActive = getCurrentValue() === index;
            const Icon = isActive ? item.activeIcon : item.icon;

            // Empty slot for FAB
            if (item.isFab) {
              return (
                <BottomNavigationAction
                  key={item.label}
                  label=""
                  icon={<Box sx={{ width: 24, height: 24 }} />}
                  disabled
                  sx={{
                    opacity: 0,
                    pointerEvents: 'none',
                  }}
                />
              );
            }

            return (
              <BottomNavigationAction
                key={item.label}
                label={item.label}
                icon={
                  item.label === 'Profile' && unreadCount > 0 ? (
                    <Badge
                      badgeContent={unreadCount}
                      color="error"
                      max={99}
                      sx={{
                        '& .MuiBadge-badge': {
                          fontSize: '0.65rem',
                          minWidth: 16,
                          height: 16,
                        },
                      }}
                    >
                      <Icon />
                    </Badge>
                  ) : (
                    <Icon />
                  )
                }
                sx={{
                  color: isActive ? 'primary.main' : 'text.secondary',
                  transition: 'all 0.2s ease',
                }}
              />
            );
          })}
        </BottomNavigation>
      </Box>
    </Paper>
  );
}

export default BottomNav;
